/**
 * AI Review tab: every AI Review run that scored this person, newest first
 * (PRD 2026-10-06, B4: "Person side panel: AI history").
 *
 * The newest run is a full card — criterion chips, the "Scored with" line and,
 * for viewers allowed to see model calls, the model call itself as tabs
 * (Request / System prompt / Response / Attempts / Details). Older runs are
 * compact cards: the same header, chips and "Scored with" line, a note when
 * the call needed its backup route, and the same tabs. Each card can also
 * open the call full screen (`PromptViewerModal`).
 *
 * The scores themselves are always visible; the model call follows the
 * audience switch (`usePromptViewerAllowed`).
 *
 * The viewer is portalled to `<body>`: the host suspends the panel while it is
 * open (`onModalChange` → `PersonPanel`'s `suspended`), which makes the drawer
 * `inert`, and a dialog inside it would be inert too.
 */
import type { PersonDetail, PersonReviewRun } from "@nu-assist/contracts";
import { Button } from "@nu-assist/ui";
import { Maximize2 } from "lucide-react";
import { Fragment, type MouseEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import {
  type AiReviewClient,
  createAiReviewClient,
  useAiReviewContactCall,
} from "../../clients/aiReview.client";
import { ScoreBadge } from "../../routes/research/import-detail/ScoreBadge";
import { displayTitle } from "../../routes/research/import-detail/importTitle";
import { PromptCallTabs, type PromptJump, contextSpanId } from "../ai-review-prompt/PromptCallTabs";
import { PromptViewerModal } from "../ai-review-prompt/PromptViewerModal";
import { usePromptViewerAllowed } from "../ai-review-prompt/access";
import type { ContextSpanKind } from "../ai-review-prompt/contextSpans";
import { EmptyLine, PanelSection } from "./PanelSection";
import {
  fmtDateTime,
  modelLabel,
  ordinal,
  personFirstName,
  personName,
  routeBadge,
} from "./format";
import { personInListPath } from "./personHandle";
import { useSeenOnce } from "./useSeenOnce";

const defaultClient = createAiReviewClient({ apiBase: import.meta.env.VITE_API_URL ?? "" });

export interface PersonAiReviewRun extends PersonReviewRun {
  /** The reviewed list's name, from the appearance that carried the run. */
  listName: string;
}

/** One entry per run (a run's parent list and its file can both be
 *  appearances of the person, each listing it), newest first. */
export function aiReviewRuns(person: PersonDetail): PersonAiReviewRun[] {
  const byId = new Map<string, PersonAiReviewRun>();
  for (const a of person.appearances ?? []) {
    for (const r of a.ai_reviews ?? []) {
      if (!byId.has(r.review_id)) byId.set(r.review_id, { ...r, listName: a.list_name });
    }
  }
  return [...byId.values()].sort((x, y) => y.created_at.localeCompare(x.created_at));
}

function runTitle(run: PersonAiReviewRun): string {
  return displayTitle(run.output_filename) || run.listName;
}

/** Where a run card leads: the review file with this person's panel open on
 *  the AI Review tab, or, while the run has no file yet, the AI Review page on
 *  the list it scored. */
export function runHref(run: PersonAiReviewRun, handle: string): string {
  return run.output_import_id
    ? personInListPath(run.output_import_id, handle, "ai-review")
    : `/research/ai-review?import=${encodeURIComponent(run.reviewed_import_id)}`;
}

function RunTitleLink({ run, handle }: { run: PersonAiReviewRun; handle: string }) {
  return (
    <Link
      to={runHref(run, handle)}
      data-testid="ai-review-run-link"
      className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {runTitle(run)}
    </Link>
  );
}

/** "us. returned ServiceUnavailableException, answered on the 2nd attempt",
 *  or null when the call answered first time on its regional route. */
export function backupRouteNote(call: PersonReviewRun["call"]): string | null {
  if (!call) return null;
  const answered = routeBadge(call.route);
  if (call.attempt_count <= 1 && answered !== "global.") return null;
  const failed = (call.attempts ?? []).find((a) => a.error_code || a.error_kind);
  const parts: string[] = [];
  if (failed) {
    parts.push(
      `${routeBadge(failed.route) ?? failed.route} returned ${failed.error_code ?? failed.error_kind}`
    );
  }
  if (call.attempt_count > 1) parts.push(`answered on the ${ordinal(call.attempt_count)} attempt`);
  if (answered === "global." && !parts.some((p) => p.includes("global."))) {
    parts.push("via the global. backup route");
  }
  return parts.join(", ") || null;
}

function RunHeader({ run, latest }: { run: PersonAiReviewRun; latest: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-micro font-semibold uppercase tracking-wide text-text-muted">
      <span data-testid="ai-review-run-header">
        AI Review · {fmtDateTime(run.created_at)} · by {run.run_by ?? "—"}
        {latest ? " · Latest" : ""}
      </span>
    </div>
  );
}

/** The contact's weighted total for this run (out of 3, two decimals as in
 *  the "AI Review Total" column). Hidden when the API has none (weights
 *  summing to zero). */
function RunTotal({ run }: { run: PersonAiReviewRun }) {
  if (run.total == null) return null;
  return (
    <span
      className="shrink-0 rounded-pill border border-border px-2 py-0.5 text-caption text-text-secondary"
      data-testid="ai-review-run-total"
    >
      Overall <span className="font-semibold tabular-nums text-text">{run.total.toFixed(2)}</span> /
      3
    </span>
  );
}

function CriterionChips({ run }: { run: PersonAiReviewRun }) {
  const cells = run.cells ?? [];
  if (cells.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Scores by criterion">
      {cells.map((cell) => (
        <li
          key={cell.criterion}
          title={cell.rationale ?? undefined}
          data-testid="ai-review-run-chip"
          className="inline-flex items-center gap-2 rounded-pill border border-border py-0.5 pl-1 pr-3 text-caption text-text-secondary"
        >
          <ScoreBadge value={cell.score} kind="score" />
          {cell.criterion}
        </li>
      ))}
    </ul>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

interface ThriveContextPart {
  target: ContextSpanKind;
  text: string;
}

/** The parts of "2 Thrive notes, 1 assessment", each the block it names.
 *  Zero (or unknown) parts are left out; empty when there is nothing to say. */
function thriveContextParts(notes: number | null, assessments: number | null): ThriveContextPart[] {
  const n = notes ?? 0;
  const a = assessments ?? 0;
  const parts: ThriveContextPart[] = [];
  if (n > 0) parts.push({ target: "notes", text: plural(n, "Thrive note", "Thrive notes") });
  if (a > 0) {
    parts.push({
      target: "assessments",
      text:
        n > 0
          ? plural(a, "assessment", "assessments")
          : plural(a, "Thrive assessment", "Thrive assessments"),
    });
  }
  return parts;
}

/** "2 Thrive notes, 1 assessment" — the Thrive context the call carried;
 *  null when there is nothing to say (no "with …" at all). */
export function thriveContextLabel(
  notes: number | null,
  assessments: number | null
): string | null {
  const parts = thriveContextParts(notes, assessments);
  return parts.length > 0 ? parts.map((p) => p.text).join(", ") : null;
}

/** One count as a link to its block in the inline model call: a real
 *  `href` (Tab + Enter), the jump itself done here. */
function ThriveContextLink({
  part,
  anchorId,
  onJump,
}: {
  part: ThriveContextPart;
  anchorId: string;
  onJump: (target: ContextSpanKind) => void;
}) {
  const jump = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onJump(part.target);
  };
  return (
    <a
      href={`#${contextSpanId(anchorId, part.target)}`}
      onClick={jump}
      aria-label={`Show the ${part.text} in the prompt`}
      data-testid={`ai-review-thrive-jump-${part.target}`}
      className="rounded-sm text-accent-ink underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {part.text}
    </a>
  );
}

function ScoredWith({
  run,
  notes,
  assessments,
  jumpTo,
}: {
  run: PersonAiReviewRun;
  notes: number | null;
  assessments: number | null;
  /** The counts link to their blocks in the card's model call (the latest
   *  card, when the call is shown); plain text otherwise. */
  jumpTo?: { anchorId: string; onJump: (target: ContextSpanKind) => void };
}) {
  const thrive = thriveContextParts(notes, assessments);
  const modelId = run.call?.model_id ?? run.model_version;
  const badge = routeBadge(run.call?.route ?? modelId);
  const version = run.call?.prompt_version ?? run.prompt_version;
  return (
    <p
      className="flex flex-wrap items-center gap-x-1 text-caption text-text-secondary"
      data-testid="ai-review-scored-with"
    >
      Scored with {modelLabel(modelId)}
      {badge && (
        <span className="rounded-sm border border-border px-1 font-mono text-micro text-text-muted">
          {badge}
        </span>
      )}
      {version && <span>· prompt {version}</span>}
      {thrive.length > 0 && (
        <span data-testid="ai-review-thrive-context">
          · with{" "}
          {thrive.map((part, i) => (
            <Fragment key={part.target}>
              {i > 0 && ", "}
              {jumpTo ? (
                <ThriveContextLink part={part} anchorId={jumpTo.anchorId} onJump={jumpTo.onJump} />
              ) : (
                part.text
              )}
            </Fragment>
          ))}
        </span>
      )}
    </p>
  );
}

function ViewPromptButton({
  run,
  onOpen,
}: {
  run: PersonAiReviewRun;
  onOpen: (run: PersonAiReviewRun) => void;
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      data-testid="ai-review-view-prompt"
      data-review-id={run.review_id}
      data-contact-id={run.import_contact_id}
      onClick={() => onOpen(run)}
    >
      <Maximize2 className="h-4 w-4" aria-hidden="true" />
      Open full screen
    </Button>
  );
}

/** The run's model call as tabs, and "Open full screen". The latest run
 *  reads its call at once; an older one when it scrolls into view
 *  (`useSeenOnce`), so a person with many runs does not read every file. */
function ModelCall({
  run,
  client,
  contactName,
  eager,
  onOpen,
  jump,
  anchorId,
}: {
  run: PersonAiReviewRun;
  client: AiReviewClient;
  contactName: string;
  eager: boolean;
  onOpen: (run: PersonAiReviewRun) => void;
  jump?: PromptJump;
  anchorId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const seen = useSeenOnce(ref, eager);
  return (
    <div
      ref={ref}
      className="flex flex-col gap-3 rounded-md border border-border bg-bg p-3"
      data-testid="ai-review-model-call"
    >
      {seen ? (
        <PromptCallTabs
          client={client}
          reviewId={run.review_id}
          contactId={run.import_contact_id}
          contactName={contactName}
          withDetails
          inline
          jump={jump}
          anchorId={anchorId}
        />
      ) : (
        <p className="py-6 text-caption text-text-muted" data-testid="ai-review-model-call-waiting">
          The prompt loads when this card is on screen.
        </p>
      )}
      <div>
        <ViewPromptButton run={run} onOpen={onOpen} />
      </div>
    </div>
  );
}

function LatestCard({
  run,
  handle,
  contactName,
  client,
  allowed,
  onOpen,
}: {
  run: PersonAiReviewRun;
  handle: string;
  contactName: string;
  client: AiReviewClient;
  allowed: boolean;
  onOpen: (run: PersonAiReviewRun) => void;
}) {
  const callQuery = useAiReviewContactCall({
    client,
    reviewId: run.review_id,
    contactId: run.import_contact_id,
    enabled: allowed && run.call != null,
  });
  const call = callQuery.data?.status === "recorded" ? callQuery.data : undefined;
  const anchorId = useId();
  const [jump, setJump] = useState<PromptJump | undefined>(undefined);
  const showsCall = allowed && run.call != null;
  const onJump = (target: ContextSpanKind) =>
    setJump((prev) => ({ target, seq: (prev?.seq ?? 0) + 1 }));
  return (
    <article
      className="flex flex-col gap-3 rounded-lg border border-accent bg-surface p-4"
      data-testid="ai-review-run-card"
      data-latest="true"
      data-review-id={run.review_id}
    >
      <RunHeader run={run} latest />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-h3 text-text">
          <RunTitleLink run={run} handle={handle} />
        </h3>
        <RunTotal run={run} />
      </div>
      <CriterionChips run={run} />
      <ScoredWith
        run={run}
        notes={call?.notes_included ?? null}
        assessments={call?.assessments_included ?? null}
        jumpTo={showsCall ? { anchorId, onJump } : undefined}
      />
      {showsCall && (
        <ModelCall
          run={run}
          client={client}
          contactName={contactName}
          eager
          onOpen={onOpen}
          jump={jump}
          anchorId={anchorId}
        />
      )}
      {!run.call && (
        <p className="text-caption text-text-muted" data-testid="ai-review-no-call">
          Scored before model calls were recorded.
        </p>
      )}
    </article>
  );
}

function CompactCard({
  run,
  handle,
  contactName,
  client,
  allowed,
  onOpen,
}: {
  run: PersonAiReviewRun;
  handle: string;
  contactName: string;
  client: AiReviewClient;
  allowed: boolean;
  onOpen: (run: PersonAiReviewRun) => void;
}) {
  const note = backupRouteNote(run.call);
  return (
    <article
      className="flex flex-col gap-2 rounded-lg border border-border bg-surface p-3"
      data-testid="ai-review-run-card"
      data-latest="false"
      data-review-id={run.review_id}
    >
      <RunHeader run={run} latest={false} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-label text-text">
          <RunTitleLink run={run} handle={handle} />
        </div>
        <RunTotal run={run} />
      </div>
      <CriterionChips run={run} />
      <ScoredWith run={run} notes={null} assessments={null} />
      {note && (
        <p className="text-caption text-warning-ink" data-testid="ai-review-backup-route">
          {note}
        </p>
      )}
      {allowed && run.call && (
        <ModelCall
          run={run}
          client={client}
          contactName={contactName}
          eager={false}
          onOpen={onOpen}
        />
      )}
    </article>
  );
}

export function AiReviewTab({
  person,
  client = defaultClient,
  onModalChange,
}: {
  person: PersonDetail;
  client?: AiReviewClient;
  /** The prompt viewer opened / closed, in the same render as the viewer. */
  onModalChange?: (open: boolean) => void;
}) {
  const runs = useMemo(() => aiReviewRuns(person), [person]);
  const allowed = usePromptViewerAllowed();
  const [open, setOpen] = useState<PersonAiReviewRun | null>(null);

  // Set in the same handler as `open`, so the host suspends the panel in the
  // same commit that mounts the viewer: the panel then still sees the "View
  // prompt" button focused, and gives focus back to it when the viewer lifts.
  const modalChange = useRef(onModalChange);
  modalChange.current = onModalChange;
  const openRef = useRef(false);
  const show = (run: PersonAiReviewRun | null) => {
    setOpen(run);
    if (openRef.current !== (run !== null)) {
      openRef.current = run !== null;
      modalChange.current?.(run !== null);
    }
  };
  // Unmounted with the viewer up: never leave the host suspended.
  useEffect(
    () => () => {
      if (openRef.current) modalChange.current?.(false);
    },
    []
  );

  if (runs.length === 0) {
    return (
      <PanelSection title="AI Review" testId="ai-review-tab">
        <EmptyLine testId="ai-review-tab-empty">
          No AI Review has scored {personFirstName(person)} yet.
        </EmptyLine>
      </PanelSection>
    );
  }

  const [latest, ...older] = runs;
  return (
    <div className="flex flex-col gap-3" data-testid="ai-review-tab">
      {latest && (
        <LatestCard
          run={latest}
          handle={person.handle}
          contactName={personName(person)}
          client={client}
          allowed={allowed}
          onOpen={show}
        />
      )}
      {older.map((run) => (
        <CompactCard
          key={run.review_id}
          run={run}
          handle={person.handle}
          contactName={personName(person)}
          client={client}
          allowed={allowed}
          onOpen={show}
        />
      ))}
      {open &&
        createPortal(
          <PromptViewerModal
            client={client}
            reviewId={open.review_id}
            contactId={open.import_contact_id}
            contactName={personName(person)}
            listTitle={runTitle(open)}
            onClose={() => show(null)}
          />,
          document.body
        )}
    </div>
  );
}
